import React, { useEffect, useState } from "react";
import "./worker_start_work.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { db } from "../firebase"; 
import { collection, query, where, doc, getDoc, setDoc, updateDoc, onSnapshot } from "firebase/firestore";

/* ===== I18N CONFIG ===== */
import i18n from "../i18n/i18n"; 

/* ===== HELPERS ===== */
const formatTime = (date) => {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

const formatDuration = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const minToTime = (totalMin) => {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const timeToMin = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return 0;
  const parts = timeStr.split(':');
  if (parts.length < 2) return 0;
  const [h, m] = parts.map(Number);
  return (h * 60) + m;
};

const nowSec = () => Math.floor(Date.now() / 1000);

const getISOWeek = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() + 4 - day);
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const WorkerStartWork = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const workerId = localStorage.getItem("workerId");

  /* --- DATE & CALENDAR --- */
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const currentYear = today.getFullYear();
  const currentMonth = pad(today.getMonth() + 1); 
  const currentDay = today.getDate().toString(); 
  const currentKw = getISOWeek();

  const daysMap = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const dayKey = daysMap[today.getDay()];

  const datePart = today.toLocaleDateString("de-DE", {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
  });
  const dayName = today.toLocaleDateString(i18n.language, { weekday: 'short' });
  const todayFormatted = `${datePart} (${dayName})`;

  /* --- STATE --- */
  const [todayProjects, setTodayProjects] = useState([]); 
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("idle");
  const [tick, setTick] = useState(0); 
  const [masterAdminId, setMasterAdminId] = useState(localStorage.getItem("masterAdminId"));

  const clearWorkData = () => {
    localStorage.removeItem("mode");
    localStorage.removeItem("workStart");
    localStorage.removeItem("pauseStart");
    localStorage.removeItem("totalPause");
    localStorage.removeItem("startTimeISO");
    localStorage.removeItem("activeProjectData");
    
    // === 1. SIGNALAS TRACKERIUI: IŠJUNGTI GPS ===
    // Tai neturi įtakos laikui, bet išjungia GPS, kad nenaudotų baterijos
    window.dispatchEvent(new Event("storage"));
  };

  /* --- AUTH & DATA LOADING --- */
  useEffect(() => {
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    const hasSession = localStorage.getItem("workerGateDone") === "true";

    if (!isRegistered) {
      navigate("/worker", { replace: true });
      return;
    }
    if (!hasSession || !workerId) {
      navigate("/worker/login", { replace: true });
      return;
    }

    const fetchWorkerState = async () => {
      try {
        const workerRef = doc(db, "workers", workerId);
        const workerSnap = await getDoc(workerRef);

        if (workerSnap.exists()) {
          const data = workerSnap.data();
          const mId = data.adminId;
          
          setMasterAdminId(mId);
          localStorage.setItem("masterAdminId", mId);
          localStorage.setItem("adminId", mId); 

          if (data.activeShift) {
            setMode(data.activeShift.mode);
            setSelectedProject(data.activeShift.project);
            localStorage.setItem("mode", data.activeShift.mode);
            localStorage.setItem("workStart", data.activeShift.workStart);
            localStorage.setItem("startTimeISO", data.activeShift.startTimeISO);
            localStorage.setItem("totalPause", String(data.activeShift.totalPause || 0));
            localStorage.setItem("activeProjectData", JSON.stringify(data.activeShift.project));
            
            if (data.activeShift.mode === 'pause' && data.activeShift.pauseStart) {
                localStorage.setItem("pauseStart", String(data.activeShift.pauseStart));
            }
          } else {
            setMode(localStorage.getItem("mode") || "idle");
          }
        }
      } catch (e) {
        console.error("Worker state error", e);
      }
    };

    fetchWorkerState();

    /* --- REAL TIME LISTENER --- */
    const q = query(
      collection(db, "projects"), 
      where("kw", "==", Number(currentKw)), 
      where("year", "==", Number(currentYear))
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const foundList = [];
      snapshot.forEach((d) => {
        const p = d.data();
        if (!p.days || !p.days[dayKey]) return;

        const isAssigned = p.days[dayKey].some(item => {
          const idInDb = typeof item === 'string' ? item : item.id;
          return idInDb === workerId;
        });

        if (isAssigned) {
          const personal = p.workerDetails?.[workerId] || {};
          foundList.push({ 
            id: d.id, 
            client: p.info.auftraggeber || "-", 
            project: p.info.bauvorhaben || "-", 
            address: p.info.adresse || "-", 
            transport: personal.transport || p.info.transport || "-",
            personalComment: personal.comment || p.info.kommentar || ""
          });
        }
      });

      setTodayProjects(foundList);
      if (foundList.length === 1 && localStorage.getItem("mode") !== "work" && localStorage.getItem("mode") !== "pause") {
          setSelectedProject(foundList[0]);
      }
      setLoading(false);
    }, (error) => {
      console.error("Error listening to projects:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [workerId, currentKw, currentYear, dayKey, navigate]);


  /* --- VISUAL CLOCK (ONLY FOR UI) --- */
  useEffect(() => {
    const timerInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerInterval);
  }, []);


  /* --- CALCULATE DISPLAY TIME --- */
  const workStart = Number(localStorage.getItem("workStart") || 0);
  const pauseStart = Number(localStorage.getItem("pauseStart") || 0);
  const totalPause = Number(localStorage.getItem("totalPause") || 0);

  let workSeconds = 0;
  let pauseSeconds = totalPause;

  if (mode === "work" && workStart) {
      workSeconds = nowSec() - workStart - totalPause;
  }
  if (mode === "pause" && pauseStart) {
    const currentPauseSegment = nowSec() - pauseStart;
    pauseSeconds = totalPause + currentPauseSegment;
    workSeconds = nowSec() - workStart - pauseSeconds;
  }
  if (workSeconds < 0) workSeconds = 0;


  /* --- BUTTON HANDLERS --- */

  const startWork = async () => {
    if (!selectedProject) return alert(t('select_project_error'));
    
    const mId = masterAdminId || localStorage.getItem("adminId");
    if (!mId) {
        alert("Klaida: Nerastas administratoriaus ID. Persijunkite programėlę arba prisijunkite iš naujo.");
        return;
    }

    const now = nowSec();
    const startTimeISO = new Date().toISOString();
    const shiftData = { mode: "work", workStart: now, startTimeISO: startTimeISO, totalPause: 0, project: selectedProject };
    
    await updateDoc(doc(db, "workers", workerId), { 
      activeShift: shiftData, 
      isWorking: true,
      startTimeISO: startTimeISO,
      lastStopISO: null 
    });
    
    localStorage.setItem("mode", "work");
    localStorage.setItem("workStart", String(now));
    localStorage.setItem("startTimeISO", startTimeISO);
    localStorage.setItem("totalPause", "0");
    localStorage.setItem("activeProjectData", JSON.stringify(selectedProject));
    localStorage.setItem("adminId", mId);
    
    // === 2. SIGNALAS TRACKERIUI: ĮJUNGTI GPS ===
    // Tai priverčia GPS pasileisti iškart, nelaukiant minutės
    window.dispatchEvent(new Event("storage"));
    
    setMode("work"); 
  };

  const togglePause = async () => {
    const workerRef = doc(db, "workers", workerId);
    if (mode === "work") {
      const pStart = nowSec();
      localStorage.setItem("mode", "pause");
      localStorage.setItem("pauseStart", String(pStart));
      await updateDoc(workerRef, { "activeShift.mode": "pause", "activeShift.pauseStart": pStart });
      setMode("pause");
    } else {
      const ps = Number(localStorage.getItem("pauseStart") || 0);
      const tp = Number(localStorage.getItem("totalPause") || 0);
      
      let addedPause = 0;
      if (ps > 0) {
          addedPause = nowSec() - ps;
      }
      if (addedPause < 0) addedPause = 0;

      const newTotalPause = tp + addedPause;
      
      localStorage.setItem("totalPause", String(newTotalPause));
      localStorage.removeItem("pauseStart");
      localStorage.setItem("mode", "work");
      
      await updateDoc(workerRef, { "activeShift.mode": "work", "activeShift.totalPause": newTotalPause, "activeShift.pauseStart": null });
      setMode("work");
    }

    // === 3. SIGNALAS TRACKERIUI: ATNAUJINTI STATUSĄ ===
    window.dispatchEvent(new Event("storage"));
  };

  const stopWork = async () => {
    if (!window.confirm(t('confirm_end_work'))) return;
    setLoading(true);

    try {
      let startTimeISO, activeProj, activeMode, shiftTotalPause, shiftPauseStart;

      const workerRef = doc(db, "workers", workerId);
      const workerSnap = await getDoc(workerRef);
      
      if (workerSnap.exists() && workerSnap.data().activeShift) {
        const s = workerSnap.data().activeShift;
        startTimeISO = s.startTimeISO;
        activeProj = s.project;
        activeMode = s.mode;
        shiftTotalPause = Number(s.totalPause || 0);
        shiftPauseStart = Number(s.pauseStart || 0);
      }

      if (!startTimeISO) {
        startTimeISO = localStorage.getItem("startTimeISO");
        const storedProj = localStorage.getItem("activeProjectData");
        activeProj = storedProj ? JSON.parse(storedProj) : null;
        activeMode = localStorage.getItem("mode");
        shiftTotalPause = Number(localStorage.getItem("totalPause") || 0);
        shiftPauseStart = Number(localStorage.getItem("pauseStart") || 0);
      }

      if (!startTimeISO || !activeProj) {
        alert(t('error_start_data_missing'));
        setLoading(false);
        return;
      }

      const startDate = new Date(startTimeISO);
      const endDate = new Date();
      
      let currentShiftRealPauseSec = shiftTotalPause;
      if (activeMode === "pause" && shiftPauseStart > 100000) {
        const lastSegment = nowSec() - shiftPauseStart;
        if (lastSegment > 0) currentShiftRealPauseSec += lastSegment;
      }

      const docId = `${workerId}_${currentYear}_${currentMonth}`;
      const logDocRef = doc(db, "monthlyWorkHours", docId);
      
      let daysData = {};
      try {
        const snap = await getDoc(logDocRef);
        if (snap.exists()) daysData = snap.data().days || {};
      } catch (e) {}
      
      let newEntry = {};
      let totalWorkDurationHours = 0; 
      let combinedPauseMin = 0; 

      if (daysData[currentDay] && daysData[currentDay].start && typeof daysData[currentDay].start === 'string') {
        
        const old = daysData[currentDay];
        
        let oldH = 8, oldM = 0;
        if (old.start.includes(':')) {
            const parts = old.start.split(':');
            oldH = Number(parts[0]);
            oldM = Number(parts[1]);
        }
        
        const firstStartDate = new Date(startDate); 
        firstStartDate.setHours(oldH, oldM, 0, 0); 
        
        const totalSpanMs = endDate - firstStartDate;
        totalWorkDurationHours = totalSpanMs / 3600000;

        let gapMin = 0;
        if (old.end) {
            const oldEndMin = timeToMin(old.end);
            const currentStartMin = (startDate.getHours() * 60) + startDate.getMinutes();
            if (currentStartMin > oldEndMin) {
                gapMin = currentStartMin - oldEndMin;
            }
        }

        let oldPauseMin = 0;
        if (old.pause) oldPauseMin = timeToMin(old.pause);
        
        const currentShiftPauseMin = Math.floor(currentShiftRealPauseSec / 60);
        combinedPauseMin = oldPauseMin + gapMin + currentShiftPauseMin; 

        let newObj = old.objekt || ""; 
        const currentProjName = activeProj.project || "";
        if (newObj && !newObj.includes(currentProjName)) {
           newObj = `${newObj}, ${currentProjName}`;
        } else if (!newObj) {
           newObj = currentProjName;
        }

        newEntry = { 
          start: old.start, 
          end: formatTime(endDate), 
          objekt: newObj, 
          sonstiges: old.sonstiges || "", 
          updatedAt: Date.now() 
        };

      } else {
        const durationMs = endDate - startDate;
        totalWorkDurationHours = durationMs / 3600000;
        
        combinedPauseMin = Math.floor(currentShiftRealPauseSec / 60);

        newEntry = { 
          start: formatTime(startDate), 
          end: formatTime(endDate), 
          objekt: activeProj.project || "-", 
          sonstiges: "", 
          updatedAt: Date.now() 
        };
      }

      let mandatoryPauseSec = 0;
      if (totalWorkDurationHours >= 10) { 
          mandatoryPauseSec = 3600; 
      } else if (totalWorkDurationHours > 9) {
          mandatoryPauseSec = 2700; 
      } else if (totalWorkDurationHours > 6) {
          mandatoryPauseSec = 1800; 
      }

      const mandatoryPauseMin = Math.floor(mandatoryPauseSec / 60);
      const finalPauseMin = Math.max(combinedPauseMin, mandatoryPauseMin);
      
      newEntry.pause = minToTime(finalPauseMin);

      daysData[currentDay] = newEntry;
      
      await setDoc(logDocRef, { workerId, adminId: masterAdminId, year: currentYear, month: currentMonth, days: daysData, updatedAt: Date.now() }, { merge: true });
      
      const stopTimeISO = new Date().toISOString();
      await updateDoc(workerRef, { activeShift: null, isWorking: false, lastSeen: Date.now(), lastStopISO: stopTimeISO });
      
      clearWorkData();
      setMode("idle");
      setSelectedProject(null);
      alert("✅ " + t('saved'));
      
      navigate("/worker/home");

    } catch (e) {
      console.error(e);
      alert(t('error_saving') + ": " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="wsw-page">
      <div className="wsw-header">
        <button className="gold-back-btn" onClick={() => navigate(-1)}>← {t('back')}</button>
        <div className="date">{todayFormatted} ({t('kw')} {currentKw})</div>
      </div>

      <div className="scroll-content">
        {loading ? ( <div className="object-card">{t('loading')}...</div> ) : (
          <>
            {mode === 'idle' && todayProjects.length === 0 && <div className="object-card empty-card">{t('no_projects')}</div>}
            {(mode !== 'idle' || selectedProject) && (
              <div className={`object-card ${mode !== 'idle' ? 'active-work-card' : 'selected-glow'}`}>
                
                {mode !== 'idle' && (
                    <div className={mode === 'pause' ? 'badge-paused' : 'badge-working'}>
                        {mode === 'work' ? t('work') : t('breakTime')}
                    </div>
                )}
                
                <div className="label-sm">{t('client')}</div>
                <div className="val-md">{selectedProject?.client || "-"}</div>
                <div className="label-sm">{t('project')}</div>
                <div className="val-lg">{selectedProject?.project}</div>
                {mode !== 'idle' && (
                  <div className="extra-info">
                    <div>📍 {selectedProject?.address}</div>
                    <div>🚗 {selectedProject?.transport || "-"}</div>
                    {selectedProject?.personalComment && <div className="pers-comment">💬 {selectedProject.personalComment}</div>}
                  </div>
                )}
              </div>
            )}
            {mode === 'idle' && todayProjects.length > 1 && (
              <div className="project-selector">
                <div className="selector-title">{t('select_project')}</div>
                <div className="mini-cards-grid">
                  {todayProjects.filter(p => p.id !== selectedProject?.id).map((proj, idx) => (
                    <div key={idx} className="mini-card" onClick={() => setSelectedProject(proj)}>
                      <strong>{proj.project}</strong><span>{proj.client}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="work-controls">
        {mode !== 'idle' && (
            <div className={mode === 'pause' ? 'gold-status-label' : 'mint-status-label'}>
                {mode === 'work' ? t('work').toUpperCase() : t('breakTime').toUpperCase()}
            </div>
        )}
        
        <div className="timer-display">{mode === 'idle' ? t('waiting') : formatDuration(mode === 'work' ? workSeconds : pauseSeconds)}</div>
        <div className="btn-group">
          {mode === "idle" ? (
            <button className={`main-btn start-btn ${!selectedProject ? 'disabled' : ''}`} onClick={startWork} disabled={!selectedProject}>▶ {t('workStart')}</button>
          ) : (
            <>
              <button className="main-btn pause-btn" onClick={togglePause}>{mode === "pause" ? "▶ " + t('work') : "Ⅱ " + t('breakTime')}</button>
              <button className="main-btn stop-btn" onClick={stopWork}>■ {t('workEnd')}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerStartWork;